import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import type { Polygon } from 'geojson';
import React from 'react';
import { useSelector } from 'react-redux';
import classifyPoint from 'robust-point-in-polygon';
import {
    TerraDraw,
    TerraDrawPolygonMode,
    TerraDrawSelectMode,
    type GeoJSONStoreFeatures
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { hasErrors } from '../../../common/enums';
import { PolygonCoords } from '../../../common/enums/maps';
import { newZone, Zones } from '../../../common/models/zones';
import { googleMapSlice } from '../../../common/store/googleMapSlice';
import { AppState, appStore, franchiseSelector, zoneSelector } from '../../../common/store/store';
import { zoneSlice } from '../../../common/store/zoneSlice';
import { deleteData, postData } from '../../api';

type HexColor = `#${string}`;
type FeatureId = string | number;

type ZoneFeature = {
    featureId: FeatureId;
    zoneId: ModelId;
};

const DEFAULT_POLYGON_COLOR: HexColor = '#000000';
const DEFAULT_OUTLINE_COLOR: HexColor = '#333333';
const SELECTED_OUTLINE_COLOR: HexColor = '#0059ff';
const DRAWING_POLYGON_COLOR: HexColor = '#1976d2';
const POLYGON_MODE = 'polygon';
const SELECT_MODE = 'select';
const COORDINATE_PRECISION = 9;

export const ZoneMap = () => {
    const map = useMap();
    const zones = useSelector((state: AppState) => zoneSelector.selectAll(state));
    const selectedZoneId = useSelector((state: AppState) => state.googleMap.selectedZone);
    const selectedZone = useSelector((state: AppState) => zoneSelector.selectById(state, selectedZoneId ?? -1));
    const franchises = useSelector((state: AppState) => franchiseSelector.selectAll(state));
    const selectedFranchise = useSelector((state: AppState) => state.googleMap.franchiseId);

    const [draw, setDraw] = React.useState<TerraDraw | undefined>();
    const [drawReady, setDrawReady] = React.useState(false);
    const [zoneFeatures, setZoneFeatures] = React.useState<ZoneFeature[]>([]);
    const [createZoneMode, setCreateZoneMode] = React.useState<boolean>(false);

    const drawRef = React.useRef<TerraDraw | undefined>();
    const zonesRef = React.useRef<Zones[]>([]);
    const zoneFeaturesRef = React.useRef<ZoneFeature[]>([]);
    const selectedZoneIdRef = React.useRef<ModelId | null>(null);
    const selectedFeatureIdRef = React.useRef<FeatureId | undefined>();
    const selectedFranchiseRef = React.useRef<ModelId | undefined>();
    const handleNewPolygonRef = React.useRef<(featureId: FeatureId) => Promise<void>>(async () => undefined);
    const handlePolygonEditedRef = React.useRef<(featureId: FeatureId) => Promise<void>>(async () => undefined);
    const handleFeatureSelectedRef = React.useRef<(featureId: FeatureId) => void>(() => undefined);
    const handleFeatureDeselectedRef = React.useRef<(featureId: FeatureId) => void>(() => undefined);

    React.useEffect(() => {
        zonesRef.current = zones;
    }, [zones]);

    React.useEffect(() => {
        zoneFeaturesRef.current = zoneFeatures;
    }, [zoneFeatures]);

    React.useEffect(() => {
        selectedZoneIdRef.current = selectedZoneId;
    }, [selectedZoneId]);

    React.useEffect(() => {
        selectedFranchiseRef.current = selectedFranchise;
    }, [selectedFranchise]);

    const getFranchiseColor = React.useCallback((franchiseId: ModelId | null | undefined): HexColor => {
        const franchise = franchises.find(p => p.id == franchiseId);
        return toHexColor(franchise?.color);
    }, [franchises]);

    const syncSelectedFeature = React.useCallback((featureId: FeatureId | undefined) => {
        const terraDraw = drawRef.current;
        const previousFeatureId = selectedFeatureIdRef.current;

        if (!terraDraw) {
            selectedFeatureIdRef.current = featureId;
            return;
        }

        if (previousFeatureId && (!featureId || !sameFeatureId(previousFeatureId, featureId))) {
            safeDeselectFeature(terraDraw, previousFeatureId);
        }

        if (featureId && terraDraw.hasFeature(featureId)) {
            safeSelectFeature(terraDraw, featureId);
            selectedFeatureIdRef.current = featureId;
        } else {
            selectedFeatureIdRef.current = undefined;
        }
    }, []);

    const handleNewPolygon = React.useCallback(async (featureId: FeatureId) => {
        const terraDraw = drawRef.current;
        if (!terraDraw) {
            return;
        }

        const feature = terraDraw.getSnapshotFeature(featureId);
        const originalPaths = featureToPaths(feature);
        if (originalPaths.length < 3) {
            return;
        }

        setCreateZoneMode(false);
        safeSetMode(terraDraw, SELECT_MODE);

        const { paths } = corrigerPaths(zonesRef.current, originalPaths);
        const normalizedPaths = normalizePaths(paths);
        const zone: Zones = {
            ...newZone,
            id: crypto.randomUUID().toString(),
            paths: normalizedPaths,
            franchiseId: selectedFranchiseRef.current ?? null
        };

        const response = await postData<Zones>('zones', zone);
        if (!hasErrors(response)) {
            safeRemoveFeatures(terraDraw, [featureId]);
            appStore.dispatch(zoneSlice.actions.addOne(response));
            appStore.dispatch(googleMapSlice.actions.selectZone(response.id));
        }
    }, []);

    const handlePolygonEdited = React.useCallback(async (featureId: FeatureId) => {
        const terraDraw = drawRef.current;
        const item = findZoneFeatureByFeatureId(zoneFeaturesRef.current, featureId);
        const zone = item ? zonesRef.current.find(p => p.id == item.zoneId) : undefined;

        if (!terraDraw || !item || !zone) {
            return;
        }

        const feature = terraDraw.getSnapshotFeature(featureId);
        const editedPaths = featureToPaths(feature);
        if (editedPaths.length < 3) {
            return;
        }

        const { fixed, paths } = corrigerPaths(zonesRef.current, editedPaths, zone.id);
        const normalizedPaths = normalizePaths(paths);
        if (fixed) {
            safeUpdateGeometry(terraDraw, featureId, pathsToPolygonGeometry(normalizedPaths));
        }

        if (pathsEqual(normalizePaths(zone.paths), normalizedPaths)) {
            return;
        }

        const response = await postData<Zones>('zones', { id: zone.id, paths: normalizedPaths });
        if (!hasErrors(response)) {
            appStore.dispatch(zoneSlice.actions.updateOne({ id: zone.id, changes: response }));
        }
    }, []);

    const handleFeatureSelected = React.useCallback((featureId: FeatureId) => {
        const item = findZoneFeatureByFeatureId(zoneFeaturesRef.current, featureId);
        const zone = item ? zonesRef.current.find(p => p.id == item.zoneId) : undefined;

        if (!item || !zone) {
            return;
        }

        if (selectedZoneIdRef.current != zone.id) {
            appStore.dispatch(googleMapSlice.actions.selectZone(zone.id));
        }

        selectedFeatureIdRef.current = featureId;
        selectedZoneIdRef.current = zone.id;
        selectedFranchiseRef.current = zone.franchiseId ?? undefined;
        appStore.dispatch(googleMapSlice.actions.setFranchise(zone.franchiseId ?? undefined));
    }, []);

    const handleFeatureDeselected = React.useCallback((featureId: FeatureId) => {
        const item = findZoneFeatureByFeatureId(zoneFeaturesRef.current, featureId);
        const deselectedCurrentFeature = sameFeatureId(selectedFeatureIdRef.current, featureId);

        if (deselectedCurrentFeature) {
            selectedFeatureIdRef.current = undefined;
        }

        if (deselectedCurrentFeature || (item && selectedZoneIdRef.current == item.zoneId)) {
            selectedZoneIdRef.current = null;
            appStore.dispatch(googleMapSlice.actions.selectZone(null));
        }
    }, []);

    React.useEffect(() => {
        handleNewPolygonRef.current = handleNewPolygon;
        handlePolygonEditedRef.current = handlePolygonEdited;
        handleFeatureSelectedRef.current = handleFeatureSelected;
        handleFeatureDeselectedRef.current = handleFeatureDeselected;
    }, [handleNewPolygon, handlePolygonEdited, handleFeatureSelected, handleFeatureDeselected]);

    React.useEffect(() => {
        if (!map) {
            return;
        }

        let terraDraw: TerraDraw | undefined;
        let projectionListener: google.maps.MapsEventListener | undefined;
        let disposed = false;

        const initialiseDraw = () => {
            if (disposed || terraDraw) {
                return;
            }

            terraDraw = new TerraDraw({
                adapter: new TerraDrawGoogleMapsAdapter({
                    lib: google.maps,
                    map,
                    isolatedData: true,
                    coordinatePrecision: COORDINATE_PRECISION
                }),
                idStrategy: {
                    getId: () => crypto.randomUUID(),
                    isValidId: (id: FeatureId) => typeof id === 'string' || typeof id === 'number'
                },
                modes: [
                    new TerraDrawPolygonMode({
                        styles: {
                            fillColor: getFeatureFillColor,
                            fillOpacity: 0.35,
                            outlineColor: DEFAULT_OUTLINE_COLOR,
                            outlineOpacity: 0.2,
                            outlineWidth: 2,
                            closingPointColor: SELECTED_OUTLINE_COLOR,
                            closingPointOutlineColor: '#ffffff',
                            coordinatePointColor: SELECTED_OUTLINE_COLOR,
                            coordinatePointOutlineColor: '#ffffff'
                        }
                    }),
                    new TerraDrawSelectMode({
                        keyEvents: {
                            delete: null,
                            deselect: 'Escape',
                            rotate: null,
                            scale: null
                        },
                        flags: {
                            polygon: {
                                feature: {
                                    draggable: false,
                                    rotateable: false,
                                    scaleable: false,
                                    coordinates: {
                                        draggable: true,
                                        midpoints: true,
                                        deletable: true
                                    }
                                }
                            }
                        },
                        styles: {
                            selectedPolygonColor: getFeatureFillColor,
                            selectedPolygonFillOpacity: 0.35,
                            selectedPolygonOutlineColor: SELECTED_OUTLINE_COLOR,
                            selectedPolygonOutlineOpacity: 1,
                            selectedPolygonOutlineWidth: 3,
                            selectionPointColor: SELECTED_OUTLINE_COLOR,
                            selectionPointOutlineColor: '#ffffff',
                            midPointColor: SELECTED_OUTLINE_COLOR,
                            midPointOutlineColor: '#ffffff'
                        }
                    })
                ]
            });

            const onReady = () => setDrawReady(true);
            const onFinish = (featureId: FeatureId, context: { mode: string; action: string; }) => {
                if (context.mode === POLYGON_MODE && context.action === 'draw') {
                    void handleNewPolygonRef.current(featureId);
                } else {
                    void handlePolygonEditedRef.current(featureId);
                }
            };
            const onSelect = (featureId: FeatureId) => handleFeatureSelectedRef.current(featureId);
            const onDeselect = (featureId: FeatureId) => handleFeatureDeselectedRef.current(featureId);

            terraDraw.on('ready', onReady);
            terraDraw.on('finish', onFinish);
            terraDraw.on('select', onSelect);
            terraDraw.on('deselect', onDeselect);
            terraDraw.start();
            safeSetMode(terraDraw, SELECT_MODE);

            drawRef.current = terraDraw;
            setDraw(terraDraw);
        };

        if (map.getProjection()) {
            initialiseDraw();
        } else {
            projectionListener = google.maps.event.addListenerOnce(map, 'projection_changed', initialiseDraw);
        }

        return () => {
            disposed = true;
            projectionListener?.remove();
            setDrawReady(false);
            setDraw(undefined);
            setZoneFeatures([]);
            zoneFeaturesRef.current = [];
            selectedFeatureIdRef.current = undefined;

            if (terraDraw) {
                terraDraw.stop();
            }

            if (drawRef.current === terraDraw) {
                drawRef.current = undefined;
            }
        };
    }, [map]);

    React.useEffect(() => {
        if (!drawReady || !draw) {
            return;
        }

        const currentFeatures = zoneFeaturesRef.current;
        const nextFeatures: ZoneFeature[] = [];

        zones.forEach(zone => {
            const existingFeature = currentFeatures.find(p => p.zoneId == zone.id);
            const featureId = existingFeature?.featureId ?? zoneFeatureId(zone.id);
            const feature = createZoneFeature(zone, featureId, getFranchiseColor(zone.franchiseId));

            if (!feature) {
                if (existingFeature) {
                    safeRemoveFeatures(draw, [existingFeature.featureId]);
                }
                return;
            }

            if (draw.hasFeature(featureId)) {
                const currentFeature = draw.getSnapshotFeature(featureId);

                if (!samePolygonGeometry(currentFeature?.geometry, feature.geometry)) {
                    safeUpdateGeometry(draw, featureId, feature.geometry);
                }

                if (currentFeature?.properties.fillColor !== feature.properties.fillColor) {
                    draw.updateFeatureProperties(featureId, { fillColor: feature.properties.fillColor });
                }
            } else {
                const validations = draw.addFeatures([feature]);
                const validation = validations[0];
                if (validation && !validation.valid) {
                    console.warn('Zone polygon ignored by Terra Draw', zone.id, validation.reason);
                    return;
                }
            }

            nextFeatures.push({ featureId, zoneId: zone.id });
        });

        currentFeatures.forEach(item => {
            if (!zones.find(zone => zone.id == item.zoneId)) {
                safeRemoveFeatures(draw, [item.featureId]);
            }
        });

        zoneFeaturesRef.current = nextFeatures;
        setZoneFeatures(nextFeatures);
    }, [draw, drawReady, getFranchiseColor, zones]);

    React.useEffect(() => {
        if (!drawReady || !draw) {
            return;
        }

        const featureId = selectedZone ? zoneFeatures.find(p => p.zoneId == selectedZone.id)?.featureId : undefined;
        syncSelectedFeature(featureId);
    }, [draw, drawReady, selectedZone, syncSelectedFeature, zoneFeatures]);

    const onFranchiseSelected = async (franchiseId: ModelId) => {
        selectedFranchiseRef.current = franchiseId;
        appStore.dispatch(googleMapSlice.actions.setFranchise(franchiseId));
        if (selectedZone && selectedZone?.franchiseId !== franchiseId) {
            const color = getFranchiseColor(franchiseId);
            const item = zoneFeaturesRef.current.find(p => p.zoneId == selectedZone.id);
            if (item && draw?.hasFeature(item.featureId)) {
                draw.updateFeatureProperties(item.featureId, { fillColor: color });
            }

            appStore.dispatch(zoneSlice.actions.updateOne({ id: selectedZone.id, changes: { franchiseId } }));
            await postData<Zones>('zones', { id: selectedZone.id, franchiseId });
        }
    };

    const onCreateButtonClick = () => {
        if (!drawReady || !draw) {
            return;
        }

        if (createZoneMode) {
            safeSetMode(draw, SELECT_MODE);
            setCreateZoneMode(false);
        } else {
            selectedZoneIdRef.current = null;
            syncSelectedFeature(undefined);
            appStore.dispatch(googleMapSlice.actions.selectZone(null));
            safeSetMode(draw, POLYGON_MODE);
            setCreateZoneMode(true);
        }
    };

    const onDeleteClick = async () => {
        if (selectedZone) {
            const item = zoneFeaturesRef.current.find(p => p.zoneId == selectedZone.id);
            if (item && draw?.hasFeature(item.featureId)) {
                safeRemoveFeatures(draw, [item.featureId]);
            }

            const nextFeatures = zoneFeaturesRef.current.filter(q => q.zoneId != selectedZone.id);
            zoneFeaturesRef.current = nextFeatures;
            setZoneFeatures(nextFeatures);

            const response = await deleteData('zones', selectedZone.id);
            if (!hasErrors(response)) {
                selectedFeatureIdRef.current = undefined;
                selectedZoneIdRef.current = null;
                appStore.dispatch(zoneSlice.actions.removeOne(selectedZone.id));
                appStore.dispatch(googleMapSlice.actions.selectZone(null));
            }
        }
    };

    return <><Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 2, p: 2 }}>
            <ButtonGroup>
                {franchises.map(p => {
                    return <Button variant={selectedFranchise === p.id ? 'contained' : 'outlined'} onClick={() => { onFranchiseSelected(p.id); }} key={p.id}>
                        <Chip size='small' label=" " sx={{ backgroundColor: p.color, m: 1 }} />{p.code}
                    </Button>;
                })}
            </ButtonGroup>
            <ButtonGroup>
                <Button variant='outlined' disabled={!drawReady} onClick={onCreateButtonClick}>{createZoneMode ? <>Annuler</> : <><Add /> Créer</>}</Button>
                <Tooltip title='Double cliquer pour supprimer'>
                    <Button variant='outlined' disabled={selectedZone == null} onDoubleClick={onDeleteClick}><Delete /> Supprimer</Button>
                </Tooltip>
            </ButtonGroup>
        </Box>
        <Box sx={{ height: '400px' }}>
            <GoogleMap
                defaultBounds={{
                    "south": 44.866238944444,
                    "west": -80.03766718924243,
                    "north": 47.89669085086322,
                    "east": -62.38263789236743
                }}
                defaultZoom={7} />
        </Box>
    </Box></>;
};

const zoneFeatureId = (zoneId: ModelId): FeatureId => `zone-${zoneId}`;

const createZoneFeature = (zone: Zones, featureId: FeatureId, fillColor: HexColor): GeoJSONStoreFeatures<Polygon> | undefined => {
    if (!zone.paths || zone.paths.length < 3) {
        return undefined;
    }

    return {
        id: featureId,
        type: 'Feature',
        geometry: pathsToPolygonGeometry(zone.paths),
        properties: {
            mode: POLYGON_MODE,
            zoneId: String(zone.id),
            fillColor
        }
    };
};

const pathsToPolygonGeometry = (paths: PolygonCoords[]): Polygon => {
    const ring = normalizePaths(paths).map(coord => [coord.lng, coord.lat]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    const closedRing = first && last && first[0] === last[0] && first[1] === last[1]
        ? ring
        : ring.concat(first ? [first] : []);

    return {
        type: 'Polygon',
        coordinates: [closedRing]
    };
};

const featureToPaths = (feature: GeoJSONStoreFeatures | undefined): PolygonCoords[] => {
    if (!feature || feature.geometry.type !== 'Polygon') {
        return [];
    }

    const ring = feature.geometry.coordinates[0] ?? [];
    const paths = ring.map(coord => ({ lng: roundCoordinate(coord[0]), lat: roundCoordinate(coord[1]) }));
    const first = paths[0];
    const last = paths[paths.length - 1];

    if (first && last && first.lat === last.lat && first.lng === last.lng) {
        paths.pop();
    }

    return normalizePaths(paths);
};

const normalizePaths = (paths: PolygonCoords[]): PolygonCoords[] => {
    return paths.map(coord => ({
        lat: roundCoordinate(coord.lat),
        lng: roundCoordinate(coord.lng)
    }));
};

const roundCoordinate = (value: number): number => {
    const scale = Math.pow(10, COORDINATE_PRECISION);
    return Math.round(value * scale) / scale;
};

const getFeatureFillColor = (feature: GeoJSONStoreFeatures): HexColor => {
    if (feature.properties.currentlyDrawing) {
        return DRAWING_POLYGON_COLOR;
    }

    const color = feature.properties.fillColor;
    return typeof color === 'string' ? toHexColor(color) : DEFAULT_POLYGON_COLOR;
};

const toHexColor = (color: string | null | undefined): HexColor => {
    if (!color) {
        return DEFAULT_POLYGON_COLOR;
    }

    const value = color.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(value)) {
        return value as HexColor;
    }

    if (/^#[0-9a-fA-F]{3}$/.test(value)) {
        return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}` as HexColor;
    }

    const rgb = value.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
    if (rgb) {
        const red = toHexByte(rgb[1]);
        const green = toHexByte(rgb[2]);
        const blue = toHexByte(rgb[3]);
        return `#${red}${green}${blue}` as HexColor;
    }

    return DEFAULT_POLYGON_COLOR;
};

const toHexByte = (value: string): string => {
    const hex = Math.min(255, Number(value)).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
};

const findZoneFeatureByFeatureId = (zoneFeatures: ZoneFeature[], featureId: FeatureId | undefined): ZoneFeature | undefined => {
    return zoneFeatures.find(p => sameFeatureId(p.featureId, featureId));
};

const sameFeatureId = (a: FeatureId | undefined, b: FeatureId | undefined): boolean => {
    return a !== undefined && b !== undefined && String(a) === String(b);
};

const samePolygonGeometry = (a: GeoJSONStoreFeatures['geometry'] | undefined, b: Polygon): boolean => {
    return Boolean(a && a.type === 'Polygon' && JSON.stringify(a.coordinates) === JSON.stringify(b.coordinates));
};

const pathsEqual = (a: PolygonCoords[] | undefined, b: PolygonCoords[]): boolean => {
    return JSON.stringify(a ?? []) === JSON.stringify(b);
};

const safeSetMode = (draw: TerraDraw, mode: string) => {
    try {
        if (draw.getMode() !== mode) {
            draw.setMode(mode);
        }
    } catch (error) {
        console.warn('Terra Draw mode change failed', error);
    }
};

const safeSelectFeature = (draw: TerraDraw, featureId: FeatureId) => {
    try {
        draw.selectFeature(featureId, SELECT_MODE);
    } catch (error) {
        console.warn('Terra Draw feature selection failed', error);
    }
};

const safeDeselectFeature = (draw: TerraDraw, featureId: FeatureId) => {
    try {
        if (draw.hasFeature(featureId)) {
            draw.deselectFeature(featureId);
        }
    } catch (error) {
        console.warn('Terra Draw feature deselection failed', error);
    }
};

const safeRemoveFeatures = (draw: TerraDraw, featureIds: FeatureId[]) => {
    const existingFeatureIds = featureIds.filter(featureId => draw.hasFeature(featureId));
    if (existingFeatureIds.length) {
        draw.removeFeatures(existingFeatureIds);
    }
};

const safeUpdateGeometry = (draw: TerraDraw, featureId: FeatureId, geometry: Polygon) => {
    try {
        if (draw.hasFeature(featureId)) {
            draw.updateFeatureGeometry(featureId, geometry);
        }
    } catch (error) {
        console.warn('Terra Draw geometry update failed', error);
    }
};

const corrigerPaths = (zones: Zones[], paths: PolygonCoords[], zoneId?: ModelId) => {
    let currentZonePath: PolygonCoords[] = [...paths];
    let fixed: boolean = false;
    for (let p of zones) {
        if (p.id !== zoneId && p.paths && p.paths.length) {
            currentZonePath = currentZonePath.map(coord => {
                if (classifyPoint(p.paths.map(q => [q.lat, q.lng]), [coord.lat, coord.lng]) == -1) {
                    fixed = true;
                    const result = p.paths.reduce((acc: { dist: number, closest: PolygonCoords; }, closest: PolygonCoords, _index: number) => {
                        var dist = Math.sqrt(Math.pow((coord.lng - closest.lng), 2) + Math.pow((coord.lat - closest.lat), 2));
                        if (dist < acc.dist) {
                            return { dist, closest };
                        } else return acc;
                    }, { dist: Number.MAX_SAFE_INTEGER, closest: coord });
                    return result.closest;
                } else {
                    return coord;
                }
            });
        }
    }
    return { fixed, paths: fixed ? currentZonePath : paths };
};
